import React, { useContext, useEffect } from 'react';
import {
  Grid, makeStyles, Chip, Box, Typography, Link, AccordionDetails,
  AccordionSummary, Accordion,
} from '@material-ui/core';
import {
  ExpandMore,
} from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { maxWidth } from '../config/config';
import AppContext from '../state/AppContext';
import StepperBooking from './Layout/StepperBooking';
import filterSubservices from '../helpers/dataManipulations';
import {
  selectedServiceIdState,
  selectedSubserviceIdState,
  selectedTherapistState, selectedTreatmentIdState,
} from '../state/selected';
import { servicesState } from '../state/apiData';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(11),
  },
  wrapperContent: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  mainCol: {
    maxWidth,
    minHeight: '100vh',
  },
  subservice: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    '&.MuiChip-root': {
      height: 'auto',
      padding: '6px 5px',
      [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
      },
    },
    '& .MuiChip-label': {
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
      textOverflow: 'clip',
    },
  },
}));

const ScreenSubservices = () => {
  const classes = useStyles();
  const intl = useIntl();

  const {
    setIsFollowUp,
    isFollowUp,
  } = useContext(AppContext);

  const setSelectedTreatmentId = useSetRecoilState(selectedTreatmentIdState);
  const [selectedTherapist, setSelectedTherapist] = useRecoilState(selectedTherapistState);
  const [selectedServiceId, setSelectedServiceId] = useRecoilState(selectedServiceIdState);
  const setSelectedSubserviceId = useSetRecoilState(selectedSubserviceIdState);
  const services = useRecoilValue(servicesState);
  const selectedService = services.filter((s) => s.externalTherapyID === selectedServiceId)[0];

  const filteredSubservices = selectedService
    ? filterSubservices(
      selectedService.subServices, selectedTherapist, isFollowUp,
    )
    : [];

  useEffect(() => {
    if (filteredSubservices.length === 1) {
      setSelectedSubserviceId(filteredSubservices[0].client_type2Id);
      setSelectedTreatmentId(filteredSubservices[0].treatment.treatmentId);
    }
  }, []);

  return (
    <div className={classes.container}>
      <StepperBooking activeStep={0} />
      <div className={classes.wrapperContent}>
        <Grid
          container
          justifyContent="center"
        >
          <Grid
            className={classes.mainCol}
            item
            xs={12}
          >

            <Box mb={2} mt={4}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                component="button"
                onClick={() => {
                  if (window && window.gtag) {
                    window.gtag('event', 'select_content', {
                      content_name: 'Back to / Reason for visit',
                      content_lang: intl.locale,
                    });
                  }
                  setSelectedServiceId(null);
                  setSelectedTherapist(null);
                  setIsFollowUp(false);
                }}
              >
                <Typography variant="subtitle2" component="span">
                  <FormattedMessage id="SelectSubservice.previousStep" />
                </Typography>
              </Link>
            </Box>

            {selectedService ? (
              <>
                <Box mb={6} mt={3}>
                  <Typography variant="h3">
                    <FormattedMessage id="SelectSubservice.title" />
                    {' '}
                    <strong>{selectedService.therapyName[intl.locale].toLowerCase()}</strong>
                    {selectedTherapist && (
                      <>
                        {' '}
                        <FormattedMessage id="SelectAppointment.with" />
                        {' '}
                        {selectedTherapist.firstName}
                        {' '}
                        {selectedTherapist.therapistName}
                      </>
                    )}
                  </Typography>
                </Box>
                {filteredSubservices.map((subS) => (
                  <Chip
                    className={classes.subservice}
                    variant="outlined"
                    color="primary"
                    label={`${subS.subServiceName[intl.locale]}${subS.treatments.length > 1 ? ` - ${subS.treatment.treatmentName[intl.locale]}` : ''}`}
                    clickable
                    key={`${subS.client_type2Id}${subS.treatment.treatmentId}`}
                    onClick={() => {
                      if (window && window.gtag) {
                        const selectedSubservice = selectedService
                        && selectedService.subServices ? selectedService
                            .subServices.filter((s) => s.client_type2Id
                            === subS.client_type2Id)[0] : null;
                        window.gtag('event', 'select_content', {
                          content_name: `${selectedService.therapyName.en.toLowerCase()} / ${selectedService.subServices.length > 1 && selectedSubservice.subServiceName.en.toLowerCase()}`,
                          content_lang: intl.locale,
                        });
                      }
                      setSelectedSubserviceId(subS.client_type2Id);
                      setSelectedTreatmentId(subS.treatment.treatmentId);
                    }}
                  />
                ))}
              </>
            ) : (
              <Box mt={4} mb={6}>
                {selectedServiceId === 'fictif-cnesst' || selectedServiceId === 'fictif-saaq' ? (
                  <Typography variant="body1">
                    <FormattedMessage
                      id="SelectSubservice.cnesstSaaq"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        i: (...chunks) => <i>{chunks}</i>,
                        h3: (...chunks) => <h3>{chunks}</h3>,
                      }}
                    />
                  </Typography>
                ) : (
                  <Typography variant="h3">
                    <FormattedMessage id="SelectSubservice.error" />
                  </Typography>
                )}
              </Box>
            )}

            <Box mt={4} mb={4}>
              <Box mb={2}>
                <Typography variant="h5">
                  <FormattedMessage id="SelectSubservice.faqTitle" />
                </Typography>
              </Box>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion1title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion1text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion2title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion2text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion3title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion3text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion4title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion4text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion5title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion5text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography className={classes.heading}>
                    <FormattedMessage
                      id="SelectSubservice.accordion6title"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    <FormattedMessage
                      id="SelectSubservice.accordion6text"
                      values={{
                        p: (...chunks) => <p>{chunks}</p>,
                        b: (...chunks) => <b>{chunks}</b>,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

ScreenSubservices.propTypes = {};

export default ScreenSubservices;
